import React, { useEffect } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useQueryParam, StringParam } from "use-query-params";
import { fetchPlans } from "../../actions/products";
import { usePrice, useProduct } from "../AccountDetails/Subscription/subscriptionUtils";

const Card = styled.div`
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 28px;
    width: 320px;
    flex-shrink: 0;
    box-shadow: 0 4px 24px rgba(0,0,0,0.06);
    font-family: "Inter", system-ui, sans-serif;
`;

const Label = styled.div`
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: #64748b;
    margin-bottom: 12px;
`;

const PlanName = styled.div`
    font-size: 1.4rem;
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 4px;
`;

const PriceRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-bottom: 20px;
`;

const Amount = styled.span`
    font-size: 2rem;
    font-weight: 800;
    color: #0f172a;
`;

const Period = styled.span`
    font-size: 0.9rem;
    color: #64748b;
`;

const Divider = styled.div`
    height: 1px;
    background: #e2e8f0;
    margin-bottom: 16px;
`;

const FeatureList = styled.ul`
    list-style: none;
    margin: 0 0 20px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Feature = styled.li`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.85rem;
    color: #374151;

    &::before {
        content: "✓";
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        background: #f0fdf4;
        border-radius: 50%;
        font-size: 0.7rem;
        color: #16a34a;
        flex-shrink: 0;
        font-weight: 700;
    }
`;

const Notice = styled.div`
    padding: 12px 14px;
    background: #f0f9ff;
    border: 1px solid #bae6fd;
    border-radius: 8px;
    font-size: 0.82rem;
    color: #0369a1;
    line-height: 1.5;
`;

export default function UserSelectedPlan() {
    const dispatch = useDispatch();
    const plans = useSelector(state => state.plans);
    const [priceId] = useQueryParam('priceId', StringParam);
    const product = useProduct(priceId);
    const price = usePrice(priceId);

    useEffect(() => {
        if (!plans.fetched) {
            dispatch(fetchPlans());
        }
    }, [dispatch, plans.fetched]);

    if (!priceId || !product || !price) return null;

    const amount = (price.unitAmountDecimal / 100).toFixed(2).replace(/\.00$/, "");
    const interval = price.recurring?.interval === "month" ? "mo" : "year";
    const isFree = price.unitAmountDecimal === 0;

    return (
        <Card>
            <Label>Your selected plan</Label>
            <PlanName>{product.name}</PlanName>
            <PriceRow>
                <Amount>£{amount}</Amount>
                <Period>/ {interval}</Period>
            </PriceRow>

            <Divider />

            <FeatureList>
                {product.features.map((f, i) => (
                    <Feature key={i}>{f.label}</Feature>
                ))}
            </FeatureList>

            <Notice>
                {isFree
                    ? "Your plan will be activated as soon as you're logged in 🎁"
                    : "You'll be redirected to checkout to complete payment 🍾"}
            </Notice>
        </Card>
    );
}
